import React, { useEffect, useState } from "react";
import { Button, IconButton, TextField, Tooltip } from "@material-ui/core";
import { AddCircleOutline, Block, Delete } from "@material-ui/icons";
import {
  Card,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls";
import { getUserById, updateUserAvailability } from "../../../../api/user";
import { alertError, alertSuccess } from "../../../../utils/logger";

const WEEK_DAYS = [
  { day: 1, label: "Lunes" },
  { day: 2, label: "Martes" },
  { day: 3, label: "Miércoles" },
  { day: 4, label: "Jueves" },
  { day: 5, label: "Viernes" },
  { day: 6, label: "Sábado" },
  { day: 7, label: "Domingo" },
];

// Igual que DEFAULT_AVAILABILITY del backend: se usa mientras el terapeuta no guarde su horario
const DEFAULT_AVAILABILITY = [1, 2, 3, 4, 5].map((day) => ({
  day,
  from: "09:00",
  to: "20:00",
}));

const rowStyle = {
  display: "flex",
  alignItems: "flex-start",
  padding: "8px 0",
  borderBottom: "1px solid #f0f0f0",
};

const rangeStyle = { display: "flex", alignItems: "center", marginBottom: 4 };

/**
 * Horario semanal en el que se pueden reservar citas con el terapeuta desde la intranet.
 */
export default function AvailabilityCard({ therapistId }) {
  const [availability, setAvailability] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!therapistId) return;
    getUserById(therapistId)
      .then((res) => {
        const saved = res.data?.availability;
        setAvailability(Array.isArray(saved) ? saved : DEFAULT_AVAILABILITY);
      })
      .catch((error) => {
        alertError({ error, customMessage: "No se ha podido cargar el horario." });
      });
  }, [therapistId]);

  function updateRange(index, field, value) {
    setAvailability(
      availability.map((range, i) => (i === index ? { ...range, [field]: value } : range)),
    );
  }

  function addRange(day) {
    const dayRanges = availability.filter((range) => range.day === day);
    const last = dayRanges[dayRanges.length - 1];
    const newRange = last ? { day, from: last.to, to: "20:00" } : { day, from: "09:00", to: "20:00" };
    setAvailability([...availability, newRange]);
  }

  function removeRange(index) {
    setAvailability(availability.filter((_, i) => i !== index));
  }

  function saveAvailability() {
    setSaving(true);
    updateUserAvailability(therapistId, availability)
      .then((res) => {
        setAvailability(res.data.availability);
        alertSuccess({ title: "Éxito!", customMessage: "Horario guardado correctamente." });
      })
      .catch((error) => {
        alertError({ error, customMessage: "No se ha podido guardar el horario." });
      })
      .finally(() => setSaving(false));
  }

  if (!availability) return null;

  return (
    <Card>
      <CardHeader title="Horario de citas" />
      <CardBody>
        <p style={{ color: "#888" }}>
          Días y horas en los que se pueden reservar citas desde la intranet (hora de Madrid).
        </p>
        {WEEK_DAYS.map(({ day, label }) => {
          const dayRanges = availability
            .map((range, index) => ({ ...range, index }))
            .filter((range) => range.day === day);
          return (
            <div key={day} style={rowStyle}>
              <strong style={{ width: 110, paddingTop: 8 }}>{label}</strong>
              <div style={{ flex: 1 }}>
                {!dayRanges.length && (
                  <span style={{ color: "#999", display: "inline-block", paddingTop: 8 }}>
                    No disponible
                  </span>
                )}
                {dayRanges.map((range) => (
                  <div key={range.index} style={rangeStyle}>
                    <TextField
                      type="time"
                      size="small"
                      variant="outlined"
                      value={range.from}
                      onChange={(e) => updateRange(range.index, "from", e.target.value)}
                      inputProps={{ step: 900 }}
                    />
                    <span style={{ margin: "0 8px" }}>–</span>
                    <TextField
                      type="time"
                      size="small"
                      variant="outlined"
                      value={range.to}
                      onChange={(e) => updateRange(range.index, "to", e.target.value)}
                      inputProps={{ step: 900 }}
                    />
                    <Tooltip title="Quitar franja">
                      <IconButton size="small" onClick={() => removeRange(range.index)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                ))}
              </div>
              <Tooltip title="Añadir franja">
                <IconButton size="small" onClick={() => addRange(day)}>
                  <AddCircleOutline fontSize="small" />
                </IconButton>
              </Tooltip>
              {dayRanges.length > 0 && (
                <Tooltip title="Marcar como no disponible">
                  <IconButton
                    size="small"
                    onClick={() =>
                      setAvailability(availability.filter((range) => range.day !== day))
                    }
                  >
                    <Block fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          );
        })}
        <Button
          onClick={saveAvailability}
          disabled={saving}
          variant="outlined"
          color="primary"
          style={{ marginTop: 16 }}
        >
          Guardar horario
        </Button>
      </CardBody>
    </Card>
  );
}
